import { FormikErrors, FormikProps, FormikTouched } from 'formik'
import { isSingleFieldsErr } from '../../../common/utils/productCreationUtil'
import {
  checkIfFormikTouchedArrayHasErr,
  checkIsErrOnRecipientOrStakeHolder,
} from '../../../common/utils/offerValidationHelper'
import type { OfferEndBuyers, OfferRecipients } from '../../../types/flyout'

export const getIsErrorForMPPOOfferTab = ({
  tabName,
  formikValues,
  eulaErr,
  mppo = true,
}: {
  tabName: 'offer-details' | 'offer-pricing' | 'offer-other'
  formikValues: FormikProps<Record<string, unknown>>
  eulaErr?: string
  mppo?: boolean
}) => {
  const { errors, touched } = formikValues
  if (formikValues?.submitCount <= 0) return false

  switch (tabName) {
    case 'offer-details': {
      const recipentErr =
        checkIfFormikTouchedArrayHasErr(
          formikValues as unknown as FormikProps<Record<string, unknown>>,
          'offerRecipients'
        ) &&
        (
          touched?.offerRecipients as unknown as FormikTouched<OfferRecipients>[]
        ).some((recipientTouched, idx) =>
          checkIsErrOnRecipientOrStakeHolder(
            recipientTouched,
            idx,
            errors?.offerRecipients as unknown as FormikErrors<OfferRecipients>[]
          )
        )

      const offerEndBuyersErr =
        checkIfFormikTouchedArrayHasErr(
          formikValues as unknown as FormikProps<Record<string, unknown>>,
          'offerEndBuyers'
        ) &&
        (
          touched?.offerEndBuyers as unknown as FormikTouched<OfferEndBuyers>[]
        ).some((recipientTouched, idx) =>
          checkIsErrOnRecipientOrStakeHolder(
            recipientTouched,
            idx,
            errors?.offerEndBuyers as unknown as FormikErrors<OfferEndBuyers>[]
          )
        )

      const resellerInfoErr = mppo
        ? checkIfFormikTouchedArrayHasErr(
            formikValues as unknown as FormikProps<Record<string, unknown>>,
            'resellerDetails'
          )
        : false

      return (
        isSingleFieldsErr(
          [
            'companyName',
            'beneficiaryId',
            'mppoName',
            'acceptBy',
            'agreementDescription',
            'offerRecipients',
            'emailCustomMessage',
            'offerStartDate',
            'offerExpirationDate',
            ...(mppo ? ['resellerDetails'] : []),
          ],
          errors as FormikErrors<Record<string, unknown>>
        ) ||
        recipentErr ||
        resellerInfoErr ||
        (mppo && offerEndBuyersErr)
      )
    }
    case 'offer-pricing': {
      const plansErr = checkIfFormikTouchedArrayHasErr(
        formikValues as unknown as FormikProps<Record<string, unknown>>,
        'plans'
      )
      return plansErr
    }
    case 'offer-other': {
      return (
        isSingleFieldsErr(
          [
            'existingContractRenewal',
            'offerStakeholders',
            'eulaType',
            'eulaFile',
            'customOfferStakeholders',
            'metaData',
          ],
          formikValues?.errors as FormikErrors<Record<string, unknown>>
        ) || !!eulaErr
      )
    }
    default: {
      return false
    }
  }
}

export const getIsErrorForAzureCombinedFormTab = ({
  tabName,
  formikValues,
  eulaErr,
}: {
  tabName: 'offer-details' | 'offer-pricing' | 'offer-other'
  formikValues: FormikProps<Record<string, unknown>>
  eulaErr?: string
}) => {
  const { errors, touched } = formikValues
  if (formikValues?.submitCount <= 0) return false

  const draftOfferErrors = (errors?.draftOffer || {}) as FormikErrors<
    Record<string, unknown>
  >
  const draftOfferTouched = (touched?.draftOffer || {}) as FormikTouched<
    Record<string, unknown>
  >

  switch (tabName) {
    case 'offer-details': {
      return isSingleFieldsErr(
        [
          'companyName',
          'mppoName',
          'beneficiaryId',
          'acceptBy',
          'offerStartDate',
          'offerExpirationDate',
          'variableStartDate',
          'preparedBy',
        ],
        draftOfferErrors
      )
    }
    case 'offer-pricing': {
      const plansErr =
        (draftOfferTouched?.plans as FormikTouched<unknown[]> | undefined)
          ?.length &&
        (draftOfferErrors?.plans as FormikErrors<unknown[]> | undefined)?.length

      return !!plansErr
    }
    case 'offer-other': {
      return (
        isSingleFieldsErr(
          ['existingContractRenewal', 'eulaFiles'],
          draftOfferErrors
        ) || !!eulaErr
      )
    }
    default: {
      return false
    }
  }
}
